import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

export type SupportedLanguage = 'fr' | 'en' | 'lg-fr' | 'sw';

interface TranslationResult {
  [lang: string]: string;
}

interface UseProductTranslationReturn {
  translateText: (text: string, productId: string, targetLang: SupportedLanguage) => Promise<string>;
  translateAllLanguages: (title: string, description: string, productId: string) => Promise<{
    titleTranslations: TranslationResult;
    descriptionTranslations: TranslationResult;
  }>;
  isTranslating: boolean;
  error: string | null;
}

/**
 * Hook pour gérer la traduction automatique des produits
 * Utilise l'Edge Function Supabase qui appelle Lovable AI
 */
export const useProductTranslation = (): UseProductTranslationReturn => {
  const [isTranslating, setIsTranslating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Traduit un texte vers une langue cible
   */
  const translateText = async (
    text: string,
    productId: string,
    targetLang: SupportedLanguage
  ): Promise<string> => {
    if (!text || text.trim() === '') {
      return text;
    }

    try {
      const { data, error: functionError } = await supabase.functions.invoke('translate-product', {
        body: {
          productId,
          title: text,
          description: '',
          targetLanguage: targetLang,
        },
      });

      if (functionError) {
        console.warn(`Translation warning for ${targetLang}:`, functionError.message);
        return text; // Fallback au texte original
      }

      return data?.title || text;
    } catch (err) {
      console.warn(`Translation error for ${targetLang}:`, err);
      return text; // Fallback au texte original
    }
  };

  /**
   * Traduit le titre et la description dans toutes les langues
   */
  const translateAllLanguages = async (
    title: string,
    description: string,
    productId: string
  ): Promise<{
    titleTranslations: TranslationResult;
    descriptionTranslations: TranslationResult;
  }> => {
    if (!title || title.trim() === '') {
      return {
        titleTranslations: { fr: '', en: '', 'lg-fr': '', sw: '' },
        descriptionTranslations: { fr: '', en: '', 'lg-fr': '', sw: '' },
      };
    }

    setIsTranslating(true);
    setError(null);

    const allLanguages: SupportedLanguage[] = ['fr', 'en', 'lg-fr', 'sw'];
    const titleTranslations: TranslationResult = {};
    const descriptionTranslations: TranslationResult = {};

    try {
      // Traduire dans toutes les langues en parallèle
      const translationPromises = allLanguages.map(async (lang) => {
        try {
          const { data, error: functionError } = await supabase.functions.invoke('translate-product', {
            body: {
              productId,
              title,
              description: description || '',
              targetLanguage: lang,
            },
          });

          if (functionError) {
            console.warn(`Translation warning for ${lang}:`, functionError.message);
            titleTranslations[lang] = title;
            descriptionTranslations[lang] = description || '';
            return;
          }

          titleTranslations[lang] = data?.title || title;
          descriptionTranslations[lang] = data?.description || description || '';
        } catch (err) {
          console.warn(`Translation error for ${lang}:`, err);
          titleTranslations[lang] = title;
          descriptionTranslations[lang] = description || '';
        }
      });

      await Promise.all(translationPromises);

      // S'assurer que toutes les langues sont présentes
      allLanguages.forEach(lang => {
        if (!titleTranslations[lang]) titleTranslations[lang] = title;
        if (!descriptionTranslations[lang]) descriptionTranslations[lang] = description || '';
      });

      return { titleTranslations, descriptionTranslations };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Translation failed';
      console.error('Translation error:', err);
      setError(errorMessage);

      // Fallback: retourner le texte original pour toutes les langues
      allLanguages.forEach(lang => {
        titleTranslations[lang] = title;
        descriptionTranslations[lang] = description || '';
      });

      return { titleTranslations, descriptionTranslations };
    } finally {
      setIsTranslating(false);
    }
  };

  return {
    translateText,
    translateAllLanguages,
    isTranslating,
    error,
  };
};

/**
 * Helper pour extraire le texte traduit dans la langue courante
 */
export const getTranslatedText = (
  translations: Record<string, string> | null | undefined,
  currentLang: SupportedLanguage,
  fallback: string = ''
): string => {
  if (!translations || typeof translations !== 'object') {
    return fallback;
  }

  // Essayer la langue courante
  if (translations[currentLang]) {
    return translations[currentLang];
  }

  // Fallback vers français
  if (translations['fr']) {
    return translations['fr'];
  }

  // Fallback vers première langue disponible
  const firstKey = Object.keys(translations)[0];
  if (firstKey && translations[firstKey]) {
    return translations[firstKey];
  }

  return fallback;
};
